import { FC, useEffect, useMemo, useRef, useState } from "react";
import { TLegacyGroup } from "../../api/types/Group";
import { TLegacyVehicle } from "../../api/types/Vehicle";
import svgIcons from "../../assets/svg/svgIcons";
import { consistentDisplayAssetItem } from "../../utils/vehicle";
import { DayStepper } from "./DayStepper";
import { EventLimitModal } from "./EventLimitModal";
import { useMultiVehicleEventCount } from "./useMultiVehicleEventCount";
import { SelectableVehicle } from "./useMultiVehicleTripData";
import { vehicleLabelFor } from "./vehicleColors";

type Props = {
  vehicles: TLegacyVehicle[];
  groups: TLegacyGroup[];
  isLoading: boolean;
  // the day being plotted, in db (unix seconds) time - the bounds the event
  // count is asked for, and the same ones the map itself is plotted from
  dayStartTimestamp: number;
  dayEndTimestamp: number;
  // device ids already on the map, so reopening the selector to add or drop
  // a vehicle doesn't start from nothing
  initialSelected?: string[];
  // whether the account has drivers set up at all - consistentDisplayAssetItem
  // drops driver name from the label entirely when this is false, same as
  // every other screen that shows an asset label
  showDriver: boolean;
  // which day is being plotted - picking it here means you don't have to
  // select vehicles first just to find out the day picker is somewhere else
  dateKey: string;
  onChangeDate: (nextDateKey: string) => void;
  // whether vehicles with no trip that day are listed. owned by the page
  // because it decides what to fetch: the default list is only vehicles with
  // a trip, filtered server-side, and the full fleet is fetched on request.
  showInactive: boolean;
  onShowInactiveChange: (next: boolean) => void;
  onConfirm: (vehicles: SelectableVehicle[]) => void;
};

// one trip request goes out per vehicle, so past this many the wait is
// worth warning about. it doesn't block the selection.
const BULK_SELECTION_NOTICE_THRESHOLD = 25;

// nothing is fetched until the user has picked - an unprompted "everything"
// would be a lot of calls
export const VehicleSelector: FC<Props> = ({
  vehicles,
  groups,
  isLoading,
  dayStartTimestamp,
  dayEndTimestamp,
  initialSelected,
  showDriver,
  dateKey,
  onChangeDate,
  showInactive,
  onShowInactiveChange,
  onConfirm,
}) => {
  const [selected, setSelected] = useState<string[]>(initialSelected ?? []);
  // every row, every group and every count asks whether an id is picked. On
  // an account with a thousand vehicles that is a million array scans per
  // render; a set makes each one a lookup.
  const selectedIds = useMemo(() => new Set(selected), [selected]);
  const [keyword, setKeyword] = useState("");
  const [mode, setMode] = useState<"vehicle" | "group">("vehicle");
  // empty means every group - narrows the vehicle list, separate from
  // picking a whole group as a selection in the By Group tab
  const [groupFilter, setGroupFilter] = useState("");
  // which groups are expanded in the By Group tab, to fine-tune membership
  // rather than only ever taking or dropping the whole group at once
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set()
  );
  // raised when plotting is attempted over the cap - the footer count says
  // so already, this is for when that's acted on anyway
  const [showEventLimitModal, setShowEventLimitModal] = useState(false);

  // how heavy the current picks are, asked ahead of the plot. the map
  // request refuses an over-cap selection, so without this the only way to
  // find out is to plot and be turned away.
  const eventCount = useMultiVehicleEventCount({
    deviceIds: selected,
    startTimestamp: dayStartTimestamp,
    endTimestamp: dayEndTimestamp,
  });

  // the default list is vehicles with a trip today. a trip start or stop
  // stamped since the day began means at least a partial trip - a completed
  // one leaves both. vehicles that only reported without moving are
  // non-trips, which the map leaves deselected anyway, so they sit behind
  // the "show all" toggle rather than padding the default view.
  const hasTripToday = (vehicle: TLegacyVehicle) =>
    vehicle.has_trips || false;

  // A different day is a different question, so the picks don't carry over.
  // The vehicle list changing is not that: it grows when the no-trip vehicles
  // are fetched and shrinks when they are dropped again, and neither should
  // deselect anything.
  const previousDayStartTimestamp = useRef(dayStartTimestamp);
  useEffect(() => {
    if (previousDayStartTimestamp.current !== dayStartTimestamp) {
      previousDayStartTimestamp.current = dayStartTimestamp;
      setSelected([]);
    }
  }, [dayStartTimestamp]);

  // A vehicle that has left the account shouldn't stay picked. Checked only
  // against the full list, because while the no-trip vehicles are filtered
  // out server-side "not listed" doesn't mean "gone".
  useEffect(() => {
    if (!showInactive || isLoading || vehicles.length === 0) return;
    const known = new Set(vehicles.map((vehicle) => vehicle.deviceid));
    setSelected((prev) => {
      const next = prev.filter((id) => known.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [showInactive, isLoading, vehicles]);

  // the account's configured asset label - asset id, description, display
  // name, driver name, in whatever combination and order the account has
  // set up. Same helper, same output, every other screen in the app shows
  // for a vehicle - deliberately not deduped here even when a field was
  // left equal to another (e.g. description never set past the asset id),
  // so this map reads identically to the Fleet Map, Camera Admin, etc.
  // rather than growing its own opinion of what counts as noise.
  const fullLabelFor = (vehicle: TLegacyVehicle) =>
    consistentDisplayAssetItem(
      vehicle.deviceid,
      vehicle.description,
      vehicle.displayname,
      vehicle.drivername,
      showDriver
    );

  const matchesKeyword = (vehicle: TLegacyVehicle) =>
    fullLabelFor(vehicle).toLowerCase().includes(keyword.toLowerCase());

  // narrowing the list to one group, rather than picking the group as a
  // selection. someone looking for a vehicle they know is in "Southeast"
  // shouldn't have to remember its number to search for it.
  const isInGroup = (vehicle: TLegacyVehicle, groupId: string) =>
    vehicle.groupid === groupId ||
    (vehicle.groups ?? []).some((group) => group.groupid === groupId);

  const filtered = useMemo(() => {
    const byGroup = groupFilter
      ? vehicles.filter((vehicle) => isInGroup(vehicle, groupFilter))
      : vehicles;
    const byKeyword = byGroup.filter(matchesKeyword);
    // the request already left the no-trip vehicles out when the toggle is
    // off; this only covers the moment before the narrower list arrives
    return showInactive ? byKeyword : byKeyword.filter(hasTripToday);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles, keyword, showInactive, dayStartTimestamp, groupFilter]);

  const toggle = (deviceId: string) =>
    setSelected((prev) =>
      prev.includes(deviceId)
        ? prev.filter((item) => item !== deviceId)
        : [...prev, deviceId]
    );

  // vehicles carry their own membership, so grouping needs no extra call.
  // groupid is the field the rest of the app treats as authoritative; the
  // groups array is only populated on some responses.
  const vehiclesInGroup = (groupId: string) =>
    vehicles.filter(
      (vehicle) =>
        vehicle.groupid === groupId ||
        (vehicle.groups ?? []).some((group) => group.groupid === groupId)
    );

  const selectableInGroup = (groupId: string) =>
    vehiclesInGroup(groupId).filter(
      (vehicle) => showInactive || hasTripToday(vehicle)
    );

  const groupSelectionState = (groupId: string) => {
    const members = selectableInGroup(groupId);
    if (members.length === 0) return "empty" as const;
    const chosen = members.filter((vehicle) =>
      selectedIds.has(vehicle.deviceid)
    );
    if (chosen.length === 0) return "none" as const;
    return chosen.length === members.length
      ? ("all" as const)
      : ("some" as const);
  };

  const toggleGroup = (groupId: string) => {
    const members = selectableInGroup(groupId).map(
      (vehicle) => vehicle.deviceid
    );
    setSelected((prev) => {
      const isFullySelected =
        members.length > 0 && members.every((id) => prev.includes(id));
      if (isFullySelected) return prev.filter((id) => !members.includes(id));
      return Array.from(new Set([...prev, ...members]));
    });
  };

  const filteredGroups = useMemo(
    () =>
      groups.filter((group) =>
        `${group.groupid} ${group.description ?? ""} ${group.displayname ?? ""}`
          .toLowerCase()
          .includes(keyword.toLowerCase())
      ),
    [groups, keyword]
  );

  // "select all" follows whatever is on screen, so a search narrows what it
  // takes rather than quietly grabbing the whole fleet
  const selectAllTargets = useMemo(() => {
    if (mode === "vehicle") return filtered.map((vehicle) => vehicle.deviceid);
    const ids = new Set<string>();
    filteredGroups.forEach((group) =>
      selectableInGroup(group.groupid).forEach((vehicle) =>
        ids.add(vehicle.deviceid)
      )
    );
    return Array.from(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, filtered, filteredGroups, showInactive, vehicles]);

  const allTargetsSelected =
    selectAllTargets.length > 0 &&
    selectAllTargets.every((id) => selectedIds.has(id));

  // what a select-all would take, and how much of it is already taken - the
  // pair the header reads out as "3 of 19 selected"
  const listedCount = selectAllTargets.length;
  const listedSelectedCount = selectAllTargets.filter((id) =>
    selectedIds.has(id)
  ).length;

  const toggleSelectAll = () =>
    setSelected((prev) =>
      allTargetsSelected
        ? prev.filter((id) => !selectAllTargets.includes(id))
        : Array.from(new Set([...prev, ...selectAllTargets]))
    );

  const confirm = () => {
    if (eventCount.isCounting) return;
    if (eventCount.isOverLimit) {
      setShowEventLimitModal(true);
      return;
    }
    onConfirm(
      selected
        .map((deviceId) => {
          const vehicle = vehicles.find((item) => item.deviceid === deviceId);
          return vehicle
            ? {
                deviceId,
                label: vehicleLabelFor(vehicle),
                fullLabel: fullLabelFor(vehicle),
              }
            : null;
        })
        .filter((item): item is SelectableVehicle => !!item)
    );
  };

  // changing the selection is the answer to the limit warning, so it also
  // dismisses it - otherwise a dismissed-then-reopened modal would come
  // back on a later selection the user never tried to plot
  useEffect(() => {
    setShowEventLimitModal(false);
  }, [selected]);

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 pt-4 pb-2 shrink-0">
        <h3 className="font-bold text-base text-accent-blue-dark">
          Select Vehicles
        </h3>
        <p className="text-xs text-heading-gray888 mt-1">
          Pick any vehicles or groups to plot, and which day.
        </p>

        <DayStepper dateKey={dateKey} onChange={onChangeDate} className="mt-3" />

        <div className="flex mt-3 rounded-lg bg-accent-blue-paleO66 p-0.5 text-xs font-medium">
          {(["vehicle", "group"] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setMode(option)}
              className={`flex-1 py-1.5 rounded-md ${
                mode === option
                  ? "bg-white text-accent-blue-dark shadow-sm"
                  : "text-heading-gray888"
              }`}
            >
              {option === "vehicle" ? "By Vehicle" : "By Group"}
            </button>
          ))}
        </div>

        <input
          type="text"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder={
            mode === "group" ? "Search groups..." : "Search vehicles..."
          }
          className="mt-3 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />

        {/* narrows which vehicles are listed. only in the vehicle tab - the
            group tab is already organised by group. */}
        {mode === "vehicle" && groups.length > 0 && (
          <select
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            className={`mt-2 w-full border rounded-lg px-2 py-2 text-sm bg-white ${
              groupFilter
                ? "border-accent-blue-dark text-accent-blue-dark"
                : "border-gray-300"
            }`}
          >
            <option value="">All groups</option>
            {groups.map((group) => (
              <option key={group.groupid} value={group.groupid}>
                {group?.displayname || group?.groupid}{group?.description ? ` | ${group.description}` : ""}
              </option>
            ))}
          </select>
        )}
        {/* no count beside it - with the list filtered server-side, the
            vehicles it would count are the ones the request deliberately
            didn't ask for */}
        <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-gray-200">
          <span className="text-xs text-heading-gray888">
            Show vehicles with no trip today
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={showInactive}
            onClick={() => onShowInactiveChange(!showInactive)}
            className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors ${
              showInactive ? "bg-accent-blue-dark" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block size-3 transform rounded-full bg-white shadow-sm transition-transform ${
                showInactive ? "translate-x-3.5" : "translate-x-0.5"
              }`}
            />
          </button>
        </div>
      </div>

      {/* Above the scrolling list rather than sticky inside it. As a sticky
          row it was repainted underneath the rows after a scroll down and
          back up, leaving a vehicle drawn across it; out here nothing can
          overlap it, and it stays reachable without scrolling to the top. */}
      {!isLoading && selectAllTargets.length > 0 && (
        <label className="shrink-0 flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-gray-50 border-t border-b border-gray-200 cursor-pointer hover:bg-accent-blue-paleO66">
          <input
            type="checkbox"
            className="size-4 accent-accent-blue-dark cursor-pointer"
            checked={allTargetsSelected}
            ref={(el) => {
              if (el)
                el.indeterminate =
                  !allTargetsSelected &&
                  selectAllTargets.some((id) => selectedIds.has(id));
            }}
            onChange={toggleSelectAll}
          />
          <span className="truncate">
            Select all {keyword ? "matching " : ""}vehicles
          </span>
          <span className="ms-auto shrink-0 font-normal text-xs text-heading-gray888">
            {listedSelectedCount} of {listedCount} selected
          </span>
        </label>
      )}

      <div
        className={`flex-grow overflow-auto ${
          !isLoading && selectAllTargets.length > 0
            ? ""
            : "border-t border-gray-200"
        }`}
      >
        {isLoading ? (
          <div className="px-4 py-6 text-xs text-heading-gray888">
            Loading vehicles&hellip;
          </div>
        ) : mode === "group" ? (
          filteredGroups.length === 0 ? (
            <div className="px-4 py-6 text-xs text-heading-gray888">
              No groups match that search.
            </div>
          ) : (
            filteredGroups.map((group) => {
              const state = groupSelectionState(group.groupid);
              const members = selectableInGroup(group.groupid);
              const memberCount = members.length;
              const selectedCount = members.filter((vehicle) =>
                selectedIds.has(vehicle.deviceid)
              ).length;
              const isDisabled = state === "empty";
              const isExpanded = expandedGroups.has(group.groupid);
              return (
                <div key={group.groupid} className="border-b border-gray-100">
                  <div
                    className={`flex items-center gap-2 px-4 py-2 text-sm ${
                      isDisabled ? "opacity-40" : "hover:bg-accent-blue-paleO66"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="size-4 accent-accent-blue-dark cursor-pointer disabled:cursor-not-allowed"
                      checked={state === "all"}
                      disabled={isDisabled}
                      ref={(el) => {
                        if (el) el.indeterminate = state === "some";
                      }}
                      onChange={() => toggleGroup(group.groupid)}
                      title={`${group?.displayname || group?.groupid}${group?.description ? ` | ${group.description}` : ""}`}
                    />
                    <span
                      className={`font-medium truncate ${isDisabled ? "" : "cursor-pointer"}`}
                      onClick={() => !isDisabled && toggleGroup(group.groupid)}
                    >
                      {group?.displayname || group?.groupid}{group?.description ? ` | ${group.description}` : ""}
                    </span>
                    <span className="text-xs text-heading-gray888 ms-auto shrink-0">
                      {memberCount === 0
                        ? "none active"
                        : selectedCount > 0
                          ? `${selectedCount} of ${memberCount} selected`
                          : `${memberCount} vehicle${memberCount === 1 ? "" : "s"}`}
                    </span>
                    {/* fine-tune membership within the group, rather than
                        only ever taking or dropping the whole thing */}
                    {!isDisabled && (
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedGroups((prev) => {
                            const next = new Set(prev);
                            if (next.has(group.groupid))
                              next.delete(group.groupid);
                            else next.add(group.groupid);
                            return next;
                          })
                        }
                        aria-label={
                          isExpanded
                            ? "Collapse group members"
                            : "Show group members"
                        }
                        className="shrink-0 size-5 flex items-center justify-center rounded hover:bg-gray-200"
                      >
                        <img
                          src={svgIcons.LeftArrowIcon}
                          alt=""
                          className={`w-2.5 h-2.5 transition-transform ${isExpanded ? "rotate-90" : "-rotate-90"}`}
                        />
                      </button>
                    )}
                  </div>

                  {isExpanded && (
                    <div className="pb-1">
                      {members.map((vehicle) => (
                        <label
                          key={vehicle.deviceid}
                          className="flex items-center gap-2 ps-9 pe-4 py-1.5 text-xs cursor-pointer hover:bg-accent-blue-paleO66"
                        >
                          <input
                            type="checkbox"
                            className="size-3.5 accent-accent-blue-dark cursor-pointer"
                            checked={selectedIds.has(vehicle.deviceid)}
                            onChange={() => toggle(vehicle.deviceid)}
                          />
                          <span className="truncate">
                            {fullLabelFor(vehicle)}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )
        ) : filtered.length === 0 ? (
          <div className="px-4 py-6 text-xs text-heading-gray888">
            {keyword || groupFilter
              ? groupFilter
                ? "No vehicles in this group match that search."
                : "No vehicles match that search."
              : showInactive
                ? "No vehicles on this account."
                : "No vehicles have a trip today. Turn on “Show vehicles with no trip today” to see the rest."}
          </div>
        ) : (
          filtered.map((vehicle) => {
            const isChecked = selectedIds.has(vehicle.deviceid);
            return (
              <label
                key={vehicle.deviceid}
                className="flex items-center gap-2 px-4 py-2 text-sm border-b border-gray-100 cursor-pointer hover:bg-accent-blue-paleO66"
              >
                <input
                  type="checkbox"
                  className="size-4 accent-accent-blue-dark cursor-pointer"
                  checked={isChecked}
                  onChange={() => toggle(vehicle.deviceid)}
                />
                <span className="font-medium truncate">
                  {fullLabelFor(vehicle)}
                </span>
              </label>
            );
          })
        )}
      </div>

      <div className="p-4 shrink-0 border-t border-gray-200 space-y-2">
        {/* what the picks weigh, next to the clear that undoes them. the
            clear takes the whole selection, not just what the current
            filter shows - the select-all checkbox above only reaches the
            visible rows, which is no help when you want to start over */}
        {selected.length > 0 && (
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              {eventCount.isCounting || eventCount.eventCount === null ? (
                <>
                  <p className="text-sm font-semibold text-heading-gray888">
                    {eventCount.isError
                      ? "Event count unavailable"
                      : "Counting events…"}
                  </p>
                  {/* the reason, not just the fact - otherwise a refused
                      request and a timed-out one read the same */}
                  {eventCount.isError && eventCount.errorMessage && (
                    <p className="text-xs text-field-error-dark break-words">
                      {eventCount.errorMessage}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p
                    className={`text-sm font-semibold ${
                      eventCount.isOverLimit
                        ? "text-field-error-dark"
                        : "text-accent-orange"
                    }`}
                  >
                    {eventCount.eventCount.toLocaleString()} Events
                  </p>
                  {eventCount.maxEvents !== null && (
                    <p className="text-xs text-heading-gray888">
                      Limit {eventCount.maxEvents.toLocaleString()} Events
                    </p>
                  )}
                </>
              )}
            </div>
            <button
              type="button"
              onClick={() => setSelected([])}
              className="text-xs font-medium text-accent-blue-bright hover:underline shrink-0"
            >
              Clear All
            </button>
          </div>
        )}
        {selected.length > listedSelectedCount && (
          <p className="text-xs text-heading-gray888">
            {selected.length} selected in total, including{" "}
            {selected.length - listedSelectedCount} not shown
          </p>
        )}
        {selected.length > BULK_SELECTION_NOTICE_THRESHOLD && (
          <p className="text-xs text-heading-gray888">
            {selected.length} vehicles is a lot to pull at once - the map may
            take a while to fill in.
          </p>
        )}
        <button
          type="button"
          disabled={selected.length === 0 || eventCount.isCounting}
          onClick={confirm}
          className="w-full py-2.5 rounded-lg bg-accent-blue-pale text-accent-blue-dark font-medium text-sm disabled:opacity-40 disabled:cursor-not-allowed enabled:hover:bg-accent-blue-paleO66"
        >
          Show {selected.length || ""} Vehicle
          {selected.length === 1 ? "" : "s"}
        </button>
      </div>

      {eventCount.eventCount !== null && eventCount.maxEvents !== null && (
        <EventLimitModal
          visible={showEventLimitModal && eventCount.isOverLimit}
          eventCount={eventCount.eventCount}
          maxEvents={eventCount.maxEvents}
          onHide={() => setShowEventLimitModal(false)}
        />
      )}
    </div>
  );
};